import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useState, useEffect, useContext } from 'react';
import { StencilContext } from "../../context";
import { ScreenReaderOnly } from "../../screen-reader-only";
import { getDateFormat, getSeparator } from "../utils";
import DateTimeSegmentElement from "./DateTimeSegment";
import { segmentInfo } from "./constants";
import { DateTimeSegment } from "./interfaces";
import { getSegmentsFromFormat } from "./utils";
import { jsx as ___EmotionJSX } from "@emotion/core";

var DateTimeSegments = function DateTimeSegments(_ref) {
  var ariaDescribedBy = _ref.ariaDescribedBy,
      ariaInvalid = _ref.ariaInvalid,
      ariaLabels = _ref.ariaLabels,
      ariaRequired = _ref.ariaRequired,
      disabled = _ref.disabled,
      id = _ref.id,
      isReadOnly = _ref.isReadOnly,
      labelId = _ref.labelId,
      locale = _ref.locale,
      onChange = _ref.onChange,
      onFirstSegmentFocus = _ref.onFirstSegmentFocus,
      segmentValues = _ref.segmentValues,
      shouldFocusFirstSegment = _ref.shouldFocusFirstSegment,
      showDate = _ref.showDate,
      showMilliseconds = _ref.showMilliseconds,
      showSeconds = _ref.showSeconds,
      showSpinButtons = _ref.showSpinButtons,
      showTime = _ref.showTime;

  var _useContext = useContext(StencilContext),
      stencilLocale = _useContext.locale;

  if (!locale) {
    locale = stencilLocale;
  }

  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      displayedSegments = _useState2[0],
      setDisplayedSegments = _useState2[1];

  var format = getDateFormat(locale);
  var separator = getSeparator(format);
  useEffect(function () {
    var dateSegments = showDate ? getSegmentsFromFormat(format, separator) : [];
    var timeSegments = showTime ? [DateTimeSegment.Hours, DateTimeSegment.Minutes].concat(_toConsumableArray(showSeconds ? [DateTimeSegment.Seconds] : []), _toConsumableArray(showMilliseconds ? [DateTimeSegment.Milliseconds] : [])) : [];
    setDisplayedSegments([].concat(_toConsumableArray(dateSegments), _toConsumableArray(timeSegments)));
  }, [showDate, showTime, showSeconds, showMilliseconds, format, separator]);

  var _useState3 = useState(-1),
      _useState4 = _slicedToArray(_useState3, 2),
      segmentToFocus = _useState4[0],
      setSegmentToFocus = _useState4[1];

  useEffect(function () {
    if (shouldFocusFirstSegment) {
      setSegmentToFocus(0);
    }
  }, [shouldFocusFirstSegment]);

  var handleFocusPreviousSegment = function handleFocusPreviousSegment(currentIndex) {
    if (currentIndex > 0) {
      setSegmentToFocus(currentIndex - 1);
    }
  };

  var handleFocusNextSegment = function handleFocusNextSegment(currentIndex) {
    if (currentIndex < displayedSegments.length - 1) {
      setSegmentToFocus(currentIndex + 1);
    }
  };

  var handleFocus = function handleFocus(focusedIndex) {
    if (focusedIndex === segmentToFocus) {
      setSegmentToFocus(-1);
    }

    if (shouldFocusFirstSegment && focusedIndex === 0) {
      onFirstSegmentFocus && onFirstSegmentFocus();
    }
  };

  var handleChange = function handleChange(newValue, segmentName) {
    var changes = _defineProperty({}, segmentName, newValue); // May need to update day value when month or year change


    if ((segmentName === DateTimeSegment.Year || segmentName === DateTimeSegment.Month) && segmentValues.day !== undefined) {
      var newMaxDay = segmentInfo.day.getMax(_objectSpread(_objectSpread({}, segmentValues), changes));
      changes[DateTimeSegment.Day] = Math.min(newMaxDay, segmentValues.day);
    }

    onChange(changes);
  };

  var renderDateTimeSegment = function renderDateTimeSegment(segment, i) {
    var ariaLabel = ariaLabels[segment] || segment;
    var ariaLabelId = "".concat(labelId, "-").concat(segment);
    var shouldFocus = i === segmentToFocus;
    var dateTimePickerProps = i === 0 ? {
      ariaDescribedBy: ariaDescribedBy,
      ariaInvalid: ariaInvalid,
      ariaRequired: ariaRequired,
      id: id
    } : {};
    return ___EmotionJSX(React.Fragment, {
      key: segment
    }, ___EmotionJSX(ScreenReaderOnly, {
      id: ariaLabelId
    }, ariaLabel), ___EmotionJSX(DateTimeSegmentElement, _extends({
      ariaLabel: ariaLabel,
      ariaLabelledBy: ariaLabelId,
      decreaseAriaLabel: ariaLabels.decrease[segment],
      disabled: disabled,
      focusNextSegment: function focusNextSegment() {
        return handleFocusNextSegment(i);
      },
      focusPreviousSegment: function focusPreviousSegment() {
        return handleFocusPreviousSegment(i);
      },
      increaseAriaLabel: ariaLabels.increase[segment],
      isReadOnly: isReadOnly,
      name: segment,
      max: segmentInfo[segment].getMax(segmentValues),
      min: segmentInfo[segment].min,
      onChange: handleChange,
      onFocus: function onFocus() {
        return handleFocus(i);
      },
      placeholder: segmentInfo[segment].placeholder,
      shouldFocus: shouldFocus,
      showSpinButtons: showSpinButtons,
      value: segmentValues[segment]
    }, dateTimePickerProps)));
  };

  return ___EmotionJSX(React.Fragment, null, displayedSegments.map(renderDateTimeSegment));
};

export default DateTimeSegments;